import React from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from '../../config';
import PageLayout from "../../components/PageLayout";
import Card from "../../components/ui/Card";
import Icon from "../../components/AppIcon";

const AdminPortal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [volunteerCount, setVolunteerCount] = React.useState(null);

  React.useEffect(() => {
    const fetchCount = async () => {
      try {
        // We reuse the /api/users endpoint which requires auth
        if (!user?.token) return;

        const res = await fetch(`${API_BASE_URL}/users`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        const data = await res.json();
        const volunteers = data.filter(u => u.role === 'volunteer');
        setVolunteerCount(volunteers.length);
      } catch (e) {
        console.error("Failed to fetch count", e);
      }
    };

    fetchCount();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <PageLayout variant="dark">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <Icon name="Shield" size={24} className="text-indigo-400" />
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">System Administration</h1>
          </div>
          <p className="text-slate-400 text-lg">
            Welcome back, <span className="text-white font-medium">{user?.email || 'Admin'}</span>
          </p>
          <p className="text-sm text-slate-500 mt-1">{currentDate}</p>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-6 py-2.5 bg-slate-800 hover:bg-red-900/30 text-slate-300 hover:text-red-400 border border-slate-700 hover:border-red-800/50 rounded-lg transition-all"
        >
          <Icon name="LogOut" size={18} />
          <span>Sign Out</span>
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {[
          { label: 'Total Volunteers', value: volunteerCount ? volunteerCount.toLocaleString() : 'Loading...', trend: '+12%', color: 'blue', icon: 'Users' },
          { label: 'Active Events', value: '42', trend: '+5', color: 'green', icon: 'Calendar' },
          { label: 'Total Hours', value: '15.4k', trend: '+8%', color: 'purple', icon: 'Clock' },
          { label: 'Partner Orgs', value: '34', trend: '+2', color: 'orange', icon: 'Building2' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-slate-800/50 border border-slate-700 p-6 rounded-xl relative overflow-hidden group hover:border-slate-600 transition-colors">
            <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-${stat.color}-400`}>
              <Icon name={stat.icon} size={64} />
            </div>
            <div className={`w-10 h-10 rounded-lg bg-${stat.color}-500/20 flex items-center justify-center mb-4 text-${stat.color}-400`}>
              <Icon name={stat.icon} size={20} />
            </div>
            <h3 className="text-slate-400 text-sm font-medium mb-1">{stat.label}</h3>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">{stat.value}</span>
              <span className="text-xs font-medium text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">{stat.trend}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions Grid */}
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <Icon name="Zap" size={20} className="text-yellow-400" />
        Quick Actions
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <button
          onClick={() => navigate('/admin/manage-volunteers')}
          className="flex flex-col items-center text-center p-8 bg-slate-800 border border-slate-700 hover:border-indigo-500/50 hover:bg-slate-800/80 rounded-xl transition-all group"
        >
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-indigo-500/20 transition-all">
            <Icon name="Users" size={32} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Manage Volunteers</h3>
          <p className="text-slate-400 text-sm">View, edit, and manage volunteer accounts and permissions.</p>
        </button>

        <button
          onClick={() => navigate('/admin/create-event')}
          className="flex flex-col items-center text-center p-8 bg-slate-800 border border-slate-700 hover:border-indigo-500/50 hover:bg-slate-800/80 rounded-xl transition-all group"
        >
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-indigo-500/20 transition-all">
            <Icon name="PlusCircle" size={32} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Create New Event</h3>
          <p className="text-slate-400 text-sm">Launch a new volunteering initiative or event.</p>
        </button>

        <button
          onClick={() => navigate('/admin/system-reports')}
          className="flex flex-col items-center text-center p-8 bg-slate-800 border border-slate-700 hover:border-indigo-500/50 hover:bg-slate-800/80 rounded-xl transition-all group"
        >
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-indigo-500/20 transition-all">
            <Icon name="BarChart2" size={32} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">System Reports</h3>
          <p className="text-slate-400 text-sm">Generate analytics and activity reports.</p>
        </button>
      </div>

      {/* Recent Activity List */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
          <h3 className="font-bold text-white">Recent System Activity</h3>
          <button className="text-sm text-indigo-400 hover:text-indigo-300">View All</button>
        </div>
        <div>
          {[
            { action: 'New Volunteer Signup', user: 'Sarah M.', time: '2 mins ago', icon: 'UserPlus' },
            { action: 'Event Created', user: 'Admin System', time: '1 hour ago', icon: 'Calendar' },
            { action: 'Hours Logged', user: 'James W.', time: '3 hours ago', icon: 'Clock' },
            { action: 'Report Generated', user: 'Admin System', time: '5 hours ago', icon: 'FileText' }
          ].map((activity, idx) => (
            <div key={idx} className="p-4 flex items-center gap-4 hover:bg-slate-800/80 border-b border-slate-700/50 last:border-0 transition-colors">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-400">
                <Icon name={activity.icon} size={18} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{activity.action}</p>
                <p className="text-xs text-slate-500">by {activity.user}</p>
              </div>
              <span className="text-xs text-slate-500 font-medium">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdminPortal;
